import { z } from "zod";
/*
 * A shared mailbox's Gmail conversation, its assignee and status (docs/specs/twin-shares.md, section 3), keyed
 * `<connection id>:<Gmail thread id>` and read and set by whoever can read the mailbox now. Its discussion is a thread
 * with no channel (`mailbox_conversation`) at /api/twin/threads. In the web app only, until Desk reads shared mailboxes
 * through the gateway.
 */
const id = z.string().trim().min(1).max(500);
export const mailboxConversationStatusSchema = z.enum(["open", "done"]);
export const mailboxConversationSchema = z.object({
    key: id,
    assigneeUserId: id.nullable(),
    status: mailboxConversationStatusSchema,
    /** Who last set the assignee or status, and when; null until anyone has. */
    updatedBy: id.nullable(),
    updatedAt: z.number().nullable(),
    /** The discussion's comments, and those the caller hasn't read. */
    commentCount: z.number().int().nonnegative(),
    unreadCount: z.number().int().nonnegative(),
}).strict();
/** `?items=`: Inbox items, comma-separated, whose conversations the Inbox shows beside them. */
export const mailboxConversationsQuerySchema = z.object({
    items: z.string().trim().min(1).max(60_000)
        .transform((value) => [...new Set(value.split(",").map((item) => item.trim()).filter(Boolean))])
        .pipe(z.array(id).min(1).max(100)),
}).strict();
/** One entry per item the caller can read in a shared mailbox; other items are left out. */
export const mailboxConversationsResponseSchema = z.object({
    conversations: z.array(z.object({ itemId: id, conversation: mailboxConversationSchema }).strict()),
}).strict();
/** One of a conversation's messages, an Inbox item, which a comment names as what it is about. */
export const mailboxConversationMessageSchema = z.object({
    id,
    sender: z.string().max(2000).nullable(),
    receivedAt: z.number(),
}).strict();
/**
 * A conversation, with the people who can read its mailbox (the only ones it can be assigned to or mention), the
 * mailbox's address, and its messages still there, newest first (at most 50).
 */
export const mailboxConversationResponseSchema = z.object({
    conversation: mailboxConversationSchema,
    readers: z.array(id),
    mailboxAddress: z.string().nullable(),
    messages: z.array(mailboxConversationMessageSchema),
}).strict();
export const mailboxConversationUpdateInputSchema = z.object({
    /** Someone who can read the mailbox, or null to leave it unassigned. */
    assigneeUserId: id.nullable().optional(),
    status: mailboxConversationStatusSchema.optional(),
}).strict().refine((input) => input.assigneeUserId !== undefined || input.status !== undefined, {
    message: "An update sets the assignee, the status, or both.",
});
